import { useEffect, useState } from 'react';
import { AUDIO_LEVELS } from './audioLevels';

const SOUND_FX_KEY = 'wp_sound_fx_enabled';
const SOUND_FX_EVENT = 'wp:sound-fx-changed';

let audioContext = null;
let masterGain = null;
let noiseBuffer = null;
let duckDepth = 0;

const lastPlayedAt = new Map();

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const hasWindow = () => typeof window !== 'undefined';

const getAudioCtor = () => {
  if (!hasWindow()) return null;
  return window.AudioContext || window.webkitAudioContext || null;
};

const ensureContext = () => {
  const AudioCtor = getAudioCtor();
  if (!AudioCtor) return null;
  if (!audioContext) {
    audioContext = new AudioCtor();
    masterGain = audioContext.createGain();
    masterGain.gain.value = duckDepth > 0 ? AUDIO_LEVELS.sfxDuck : AUDIO_LEVELS.sfxMaster;
    masterGain.connect(audioContext.destination);
  }
  if (audioContext.state === 'suspended') {
    audioContext.resume().catch(() => {});
  }
  return audioContext;
};

const syncMasterGain = () => {
  const context = ensureContext();
  if (!context || !masterGain) return;
  const nextGain = duckDepth > 0 ? AUDIO_LEVELS.sfxDuck : AUDIO_LEVELS.sfxMaster;
  masterGain.gain.cancelScheduledValues(context.currentTime);
  masterGain.gain.setTargetAtTime(nextGain, context.currentTime, 0.025);
};

const scheduleEnvelope = (gainNode, at, duration, peak, attack = 0.004, release = 0.08) => {
  const safePeak = clamp(peak, 0.0001, 1);
  const attackEnd = at + Math.max(attack, 0.001);
  const releaseStart = Math.max(attackEnd, at + Math.max(duration - release, 0.001));
  gainNode.gain.cancelScheduledValues(at);
  gainNode.gain.setValueAtTime(0.0001, at);
  gainNode.gain.linearRampToValueAtTime(safePeak, attackEnd);
  gainNode.gain.exponentialRampToValueAtTime(Math.max(0.0001, safePeak * 0.55), releaseStart);
  gainNode.gain.exponentialRampToValueAtTime(0.0001, at + duration);
};

const getNoiseBuffer = (context) => {
  if (noiseBuffer) return noiseBuffer;
  const buffer = context.createBuffer(1, Math.floor(context.sampleRate * 1.5), context.sampleRate);
  const channel = buffer.getChannelData(0);
  for (let i = 0; i < channel.length; i += 1) {
    channel[i] = (Math.random() * 2 - 1) * (1 - i / channel.length);
  }
  noiseBuffer = buffer;
  return buffer;
};

const playTone = ({
  at,
  duration = 0.08,
  frequency = 440,
  glideTo = null,
  type = 'sine',
  volume = 0.08,
  attack = 0.004,
  release = 0.08,
  detune = 0,
  highpass = null,
  lowpass = null,
}) => {
  const context = ensureContext();
  if (!context || !masterGain) return;

  const osc = context.createOscillator();
  const gain = context.createGain();

  osc.type = type;
  osc.frequency.setValueAtTime(Math.max(24, frequency), at);
  osc.detune.setValueAtTime(detune + (Math.random() * 8 - 4), at);

  if (glideTo && glideTo > 0) {
    osc.frequency.exponentialRampToValueAtTime(Math.max(24, glideTo), at + duration);
  }

  let tail = osc;

  if (highpass) {
    const filter = context.createBiquadFilter();
    filter.type = 'highpass';
    filter.frequency.setValueAtTime(Math.max(30, highpass), at);
    tail.connect(filter);
    tail = filter;
  }

  if (lowpass) {
    const filter = context.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(Math.max(80, lowpass), at);
    tail.connect(filter);
    tail = filter;
  }

  tail.connect(gain);
  gain.connect(masterGain);

  scheduleEnvelope(gain, at, duration, volume, attack, release);
  osc.start(at);
  osc.stop(at + duration + 0.02);
};

const playNoise = ({
  at,
  duration = 0.12,
  volume = 0.05,
  attack = 0.002,
  release = 0.08,
  highpassStart = 180,
  highpassEnd = null,
  lowpassStart = 6500,
  lowpassEnd = null,
}) => {
  const context = ensureContext();
  if (!context || !masterGain) return;

  const source = context.createBufferSource();
  source.buffer = getNoiseBuffer(context);

  const highpass = context.createBiquadFilter();
  highpass.type = 'highpass';
  highpass.frequency.setValueAtTime(Math.max(30, highpassStart), at);
  highpass.frequency.exponentialRampToValueAtTime(Math.max(30, highpassEnd || highpassStart), at + duration);

  const lowpass = context.createBiquadFilter();
  lowpass.type = 'lowpass';
  lowpass.frequency.setValueAtTime(Math.max(100, lowpassStart), at);
  lowpass.frequency.exponentialRampToValueAtTime(Math.max(100, lowpassEnd || lowpassStart), at + duration);

  const gain = context.createGain();

  source.connect(highpass);
  highpass.connect(lowpass);
  lowpass.connect(gain);
  gain.connect(masterGain);

  scheduleEnvelope(gain, at, duration, volume, attack, release);
  source.start(at);
  source.stop(at + duration + 0.02);
};

const playPluck = (at, frequency, volume, brightness = 1) => {
  playTone({
    at,
    duration: 0.085,
    frequency,
    glideTo: frequency * 0.82,
    type: 'triangle',
    volume: 0.075 * volume,
    attack: 0.002,
    release: 0.07,
    highpass: 120,
    lowpass: 2800 * brightness,
  });
  playTone({
    at,
    duration: 0.03,
    frequency: frequency * 1.98,
    glideTo: frequency * 1.4,
    type: 'square',
    volume: 0.022 * volume,
    attack: 0.001,
    release: 0.025,
    highpass: 500,
    lowpass: 3600 * brightness,
  });
};

const playThunk = (at, frequency, volume) => {
  playTone({
    at,
    duration: 0.12,
    frequency,
    glideTo: frequency * 0.58,
    type: 'triangle',
    volume: 0.1 * volume,
    attack: 0.002,
    release: 0.1,
    highpass: 40,
    lowpass: 1100,
  });
  playNoise({
    at,
    duration: 0.03,
    volume: 0.028 * volume,
    attack: 0.001,
    release: 0.03,
    highpassStart: 220,
    highpassEnd: 900,
    lowpassStart: 1900,
    lowpassEnd: 800,
  });
};

const playChime = (at, notes, volume, step = 0.06) => {
  notes.forEach((frequency, index) => {
    playTone({
      at: at + index * step,
      duration: 0.22,
      frequency,
      type: 'sine',
      volume: 0.06 * volume,
      attack: 0.002,
      release: 0.16,
      highpass: 160,
      lowpass: 5200,
    });
    playTone({
      at: at + index * step,
      duration: 0.12,
      frequency: frequency * 2,
      type: 'triangle',
      volume: 0.015 * volume,
      attack: 0.002,
      release: 0.1,
      highpass: 600,
      lowpass: 6200,
    });
  });
};

const playWhoosh = (at, duration, volume, from = 180, to = 2200) => {
  playNoise({
    at,
    duration,
    volume: 0.055 * volume,
    attack: 0.002,
    release: Math.min(0.09, duration * 0.55),
    highpassStart: from,
    highpassEnd: to,
    lowpassStart: 7000,
    lowpassEnd: 1200,
  });
};

const SOUND_RECIPES = {
  softClick: ({ at, volume }) => playPluck(at, 620, 0.85 * volume, 0.95),
  learnNowOpen: ({ at, volume }) => {
    playWhoosh(at, 0.11, 0.42 * volume, 260, 2100);
    playChime(at + 0.028, [360, 520, 760], 0.76 * volume, 0.045);
  },
  previewToggle: ({ at, volume }) => {
    playPluck(at, 520, 0.56 * volume, 0.96);
    playPluck(at + 0.03, 690, 0.44 * volume, 1.08);
  },
  flashcardMove: ({ at, volume }) => {
    playWhoosh(at, 0.075, 0.34 * volume, 360, 1600);
    playPluck(at + 0.022, 470, 0.54 * volume, 0.94);
  },
  flashcardFlip: ({ at, volume }) => {
    playPluck(at, 430, 0.44 * volume, 0.92);
    playPluck(at + 0.045, 760, 0.62 * volume, 1.1);
  },
  practiceStage1: ({ at, volume }) => playPluck(at, 620, 0.74 * volume, 1),
  practiceStage2: ({ at, volume }) => playChime(at, [520, 760], 0.8 * volume, 0.05),
  practiceStage3: ({ at, volume }) => {
    playWhoosh(at, 0.085, 0.38 * volume, 240, 2400);
    playChime(at + 0.028, [470, 680, 980], 0.84 * volume, 0.045);
  },
  practiceStage4: ({ at, volume }) => {
    playWhoosh(at, 0.11, 0.38 * volume, 220, 2500);
    playChime(at + 0.024, [580, 820, 1120], 0.82 * volume, 0.045);
    playTone({ at: at + 0.052, duration: 0.13, frequency: 980, glideTo: 1380, type: 'sine', volume: 0.02 * volume, release: 0.11, highpass: 360, lowpass: 3600 });
  },
  sectionAdvance: ({ at, volume }) => {
    playWhoosh(at, 0.12, 0.8 * volume, 240, 2600);
    playPluck(at + 0.045, 560, 0.65 * volume, 1.1);
  },
  sectionComplete: ({ at, volume }) => playChime(at, [560, 760, 980], 0.95 * volume, 0.05),
  fillBlankTap: ({ at, volume }) => playPluck(at, 760, 0.6 * volume, 1.15),
  readyTap: ({ at, volume }) => playPluck(at, 680, 0.45 * volume, 1.05),
  submitCommit: ({ at, volume }) => playThunk(at, 180, 0.9 * volume),
  processingWhoosh: ({ at, volume }) => {
    playWhoosh(at, 0.17, volume, 160, 3200);
    playTone({ at: at + 0.03, duration: 0.1, frequency: 360, glideTo: 540, type: 'sine', volume: 0.035 * volume, release: 0.08 });
  },
  saveRise: ({ at, volume }) => playChime(at, [400, 520, 680], 0.7 * volume, 0.045),
  resultHigh: ({ at, volume }) => playChime(at, [660, 990, 1320, 1760], volume, 0.07),
  resultMid: ({ at, volume }) => playChime(at, [392, 523, 659], 0.75 * volume, 0.08),
  resultLow: ({ at, volume }) => {
    playTone({ at, duration: 0.22, frequency: 280, glideTo: 220, type: 'triangle', volume: 0.09 * volume, release: 0.18, lowpass: 1600 });
    playTone({ at: at + 0.05, duration: 0.18, frequency: 340, glideTo: 250, type: 'sine', volume: 0.03 * volume, release: 0.14, highpass: 120, lowpass: 2000 });
  },
  correct: ({ at, volume }) => playChime(at, [620, 940], 0.95 * volume, 0.05),
  wrong: ({ at, volume }) => {
    playThunk(at, 220, 0.7 * volume);
    playTone({ at: at + 0.01, duration: 0.1, frequency: 260, glideTo: 190, type: 'triangle', volume: 0.035 * volume, release: 0.09, lowpass: 1200 });
  },
  sparkle: ({ at, volume }) => playChime(at, [880, 1240, 1660], 0.8 * volume, 0.035),
  pick: ({ at, volume }) => playPluck(at, 540, 0.72 * volume, 1.08),
  dropBack: ({ at, volume }) => playPluck(at, 360, 0.62 * volume, 0.86),
  check: ({ at, volume }) => playThunk(at, 250, 0.78 * volume),
  hintOpen: ({ at, volume }) => playChime(at, [480, 640], 0.6 * volume, 0.05),
  challengeReset: ({ at, volume }) => {
    playTone({ at, duration: 0.08, frequency: 420, glideTo: 320, type: 'triangle', volume: 0.05 * volume, release: 0.06, lowpass: 1800 });
    playTone({ at: at + 0.07, duration: 0.12, frequency: 420, glideTo: 620, type: 'triangle', volume: 0.055 * volume, release: 0.08, lowpass: 2000 });
  },
  micWindowAlert: ({ at, volume }) => {
    playChime(at, [660, 990], 1.05 * volume, 0.07);
    playChime(at + 0.24, [740, 1110], 0.95 * volume, 0.07);
  },
  dictType: ({ at, volume }) => playTone({ at, duration: 0.03, frequency: 900, glideTo: 820, type: 'square', volume: 0.02 * volume, attack: 0.001, release: 0.02, highpass: 500, lowpass: 2600 }),
  dictBackspace: ({ at, volume }) => playTone({ at, duration: 0.035, frequency: 500, glideTo: 420, type: 'square', volume: 0.022 * volume, attack: 0.001, release: 0.025, highpass: 220, lowpass: 1800 }),
  dictSubmit: ({ at, volume }) => playChime(at, [500, 720], 0.58 * volume, 0.04),
  dictCorrect: ({ at, volume }) => playChime(at, [700, 980, 1320], 0.92 * volume, 0.045),
  dictWrong: ({ at, volume }) => playThunk(at, 210, 0.75 * volume),
  nextCard: ({ at, volume }) => {
    playWhoosh(at, 0.09, 0.5 * volume, 280, 1400);
    playPluck(at + 0.03, 460, 0.45 * volume, 0.92);
  },
  modeEnter: ({ at, volume }) => playChime(at, [360, 520, 760], 0.82 * volume, 0.05),
  retryLoop: ({ at, volume }) => playPluck(at, 420, 0.6 * volume, 0.9),
  mastery: ({ at, volume }) => playChime(at, [520, 780, 1040, 1560], volume, 0.05),
  continue: ({ at, volume }) => playPluck(at, 610, 0.55 * volume, 1.02),
  muteClick: ({ at, volume }) => playThunk(at, 190, 0.55 * volume),
  replayTick: ({ at, volume }) => playChime(at, [620, 620], 0.52 * volume, 0.045),
  bookmarkOn: ({ at, volume }) => playChime(at, [560, 820], 0.7 * volume, 0.05),
  bookmarkOff: ({ at, volume }) => {
    playTone({ at, duration: 0.08, frequency: 420, glideTo: 320, type: 'triangle', volume: 0.045 * volume, release: 0.06, lowpass: 1800 });
    playTone({ at: at + 0.03, duration: 0.08, frequency: 320, glideTo: 260, type: 'triangle', volume: 0.03 * volume, release: 0.06, lowpass: 1500 });
  },
  saveWord: ({ at, volume }) => playChime(at, [480, 620, 820], 0.78 * volume, 0.045),
  alreadySaved: ({ at, volume }) => playPluck(at, 500, 0.48 * volume, 0.95),
  rescore: ({ at, volume }) => {
    playThunk(at, 210, 0.72 * volume);
    playTone({ at: at + 0.05, duration: 0.09, frequency: 410, glideTo: 520, type: 'sine', volume: 0.025 * volume, release: 0.07, highpass: 180, lowpass: 2200 });
  },
  refresh: ({ at, volume }) => playChime(at, [520, 700], 0.65 * volume, 0.045),
  softFail: ({ at, volume }) => {
    playTone({ at, duration: 0.12, frequency: 250, glideTo: 180, type: 'triangle', volume: 0.05 * volume, release: 0.1, lowpass: 1400 });
    playNoise({ at, duration: 0.05, volume: 0.015 * volume, attack: 0.001, release: 0.04, highpassStart: 180, highpassEnd: 420, lowpassStart: 900, lowpassEnd: 500 });
  },
};

export const isSoundEffectsEnabled = () => {
  if (!hasWindow()) return true;
  try {
    const value = window.localStorage.getItem(SOUND_FX_KEY);
    return value !== '0' && value !== 'false';
  } catch (_) {
    return true;
  }
};

export const setSoundEffectsEnabled = (enabled) => {
  if (!hasWindow()) return;
  try {
    window.localStorage.setItem(SOUND_FX_KEY, enabled ? '1' : '0');
  } catch (_) {}
  try {
    window.dispatchEvent(new CustomEvent(SOUND_FX_EVENT, { detail: enabled }));
  } catch (_) {}
};

export const useSoundEffectsEnabled = () => {
  const [enabled, setEnabled] = useState(() => isSoundEffectsEnabled());

  useEffect(() => {
    if (!hasWindow()) return undefined;
    const sync = () => setEnabled(isSoundEffectsEnabled());
    window.addEventListener(SOUND_FX_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(SOUND_FX_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  return enabled;
};

export const duckSoundEffects = () => {
  duckDepth += 1;
  syncMasterGain();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    duckDepth = Math.max(0, duckDepth - 1);
    syncMasterGain();
  };
};

export const primeSoundEffects = () => {
  if (!isSoundEffectsEnabled()) return false;
  return Boolean(ensureContext());
};

export const playSoundEffect = (name, options = {}) => {
  if (!isSoundEffectsEnabled()) return false;

  const {
    throttleMs = 0,
    throttleKey = name,
    volume = 1,
  } = options;

  if (throttleMs > 0 && throttleKey) {
    const now = Date.now();
    const previous = lastPlayedAt.get(throttleKey) || 0;
    if (now - previous < throttleMs) return false;
    lastPlayedAt.set(throttleKey, now);
  }

  const context = ensureContext();
  if (!context) return false;

  const recipe = SOUND_RECIPES[name] || SOUND_RECIPES.softClick;
  recipe({
    at: context.currentTime + 0.002,
    volume: clamp(volume, 0, 2),
  });
  return true;
};
